/**
 * Shared JE rendering primitives — extracted in Phase 6a.18 so both the
 * engagement-level Yevmiye tab (Phase 6a.15 / 6a.15a) and the per-contract
 * detail page Section 3 (Phase 6a.18) can render the same JE Card
 * accordion + monthly grouping + consolidated summary table without
 * duplicating ~400 lines of presentation code.
 *
 * The engagement-level Yevmiye tab passes through default props — it
 * still shows the monthly Big4 consolidated summary table at the foot of
 * each open month accordion AND the Phase 6a.18 per-contract drill-down
 * placeholder under each JE card. The per-contract page opts out of both
 * (`showMonthlySummary={false}` because consolidations are an
 * engagement-level concept; `showPerContractPlaceholder={false}` because
 * the page itself IS the per-contract drill-down).
 *
 * Helpers also exported here (single source of truth):
 *   • mapAccountToTr   — backend English account → TR auditor label
 *   • categorizeAccount — EXPENSE | BALANCE_SHEET | CASH bucket
 *   • consolidateJEs   — per-account Dr/Cr aggregation grouped by category
 *   • sumDrCr          — flat Dr / Cr sums across a JE list
 *   • ConsolidatedSummaryTable — Big4 audit working-paper rollup table
 */

import { format, parseISO } from "date-fns";
import { tr as trLocale, enUS } from "date-fns/locale";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { JELinesTable } from "@/features/engagement/components/JELinesTable";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import i18n from "@/i18n";
import { accountExplainerKey } from "@/lib/accountExplainerKey";
import { formatCurrency } from "@/lib/format";
import { mapAccountLabel } from "@/lib/i18n/accountLabels";
import { tieOutVariant } from "@/components/common/TieOutBadge";
import { buildContractDetailPath } from "@/pages/engagement/utils/contractDetailLink";
import { cn } from "@/lib/utils";
import type { AggregatedJournalEntry } from "@/types";

// ─── i18n helper signature ─────────────────────────────────────────────────

export type TFn = (key: string, params?: Record<string, unknown>) => string;

// ─── Account TR mapping ────────────────────────────────────────────────────

/**
 * SPEC-08 §6b — delegate to the central account-label map. The map
 * covers ALL accounts the backend can emit (per-class ROU variants,
 * FX gain/loss, restoration, termination, reclassification, retained
 * earnings, cash sub-accounts) and warns in dev on any unmapped name —
 * so a raw English string can no longer reach the UI. Pre-SPEC-08 this
 * function carried an in-file partial coverage list; that list has been
 * replaced by the central map.
 */
export function mapAccountToTr(accountEn: string, t: TFn): string {
  // The central map uses the same `t`-with-defaultValue contract as
  // before. Cast keeps the existing TFn signature compatible.
  return mapAccountLabel(
    accountEn,
    t as unknown as Parameters<typeof mapAccountLabel>[1],
  );
}

// ─── Consolidation helpers ─────────────────────────────────────────────────

export type AccountCategory = "EXPENSE" | "BALANCE_SHEET" | "CASH";

export const ACCOUNT_CATEGORY_ORDER: AccountCategory[] = [
  "EXPENSE",
  "BALANCE_SHEET",
  "CASH",
];

export interface AccountConsolidation {
  accountEn: string;
  accountTr: string;
  category: AccountCategory;
  debit: number;
  credit: number;
}

export interface ConsolidatedSummary {
  accountsByCategory: Map<AccountCategory, AccountConsolidation[]>;
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
}

export function categorizeAccount(accountEn: string): AccountCategory {
  if (
    accountEn.startsWith("Interest Expense") ||
    accountEn.startsWith("Depreciation Expense") ||
    accountEn.startsWith("Variable Lease Expense")
  ) {
    return "EXPENSE";
  }
  if (
    accountEn.startsWith("Right-of-Use Asset") ||
    accountEn.startsWith("Accumulated Depreciation") ||
    accountEn === "Lease Liability"
  ) {
    return "BALANCE_SHEET";
  }
  if (
    accountEn === "Cash / Bank" ||
    accountEn === "Cash" ||
    accountEn === "Bank"
  ) {
    return "CASH";
  }
  return "BALANCE_SHEET";
}

export function consolidateJEs(
  jes: AggregatedJournalEntry[],
  t: TFn,
): ConsolidatedSummary {
  const accountMap = new Map<string, AccountConsolidation>();
  for (const je of jes) {
    for (const line of je.lines) {
      const existing = accountMap.get(line.account);
      if (existing) {
        existing.debit += Number(line.debit) || 0;
        existing.credit += Number(line.credit) || 0;
      } else {
        accountMap.set(line.account, {
          accountEn: line.account,
          accountTr: mapAccountToTr(line.account, t),
          category: categorizeAccount(line.account),
          debit: Number(line.debit) || 0,
          credit: Number(line.credit) || 0,
        });
      }
    }
  }

  const accountsByCategory = new Map<AccountCategory, AccountConsolidation[]>();
  for (const cat of ACCOUNT_CATEGORY_ORDER) {
    const items = [...accountMap.values()]
      .filter((a) => a.category === cat)
      .sort((a, b) => a.accountTr.localeCompare(b.accountTr, "tr"));
    if (items.length > 0) accountsByCategory.set(cat, items);
  }

  let totalDebit = 0;
  let totalCredit = 0;
  for (const a of accountMap.values()) {
    totalDebit += a.debit;
    totalCredit += a.credit;
  }
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return { accountsByCategory, totalDebit, totalCredit, balanced };
}

export function sumDrCr(jes: AggregatedJournalEntry[]): {
  totalDebit: number;
  totalCredit: number;
} {
  let debit = 0;
  let credit = 0;
  for (const je of jes) {
    for (const line of je.lines) {
      debit += Number(line.debit) || 0;
      credit += Number(line.credit) || 0;
    }
  }
  return { totalDebit: debit, totalCredit: credit };
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ─── ConsolidatedSummaryTable ──────────────────────────────────────────────

interface ConsolidatedSummaryTableProps {
  summary: ConsolidatedSummary;
  totalLabel: string;
  currency: string;
  testIdPrefix: string;
}

export function ConsolidatedSummaryTable({
  summary,
  totalLabel,
  currency,
  testIdPrefix,
}: ConsolidatedSummaryTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto w-full">
    <Table className="min-w-max" data-testid={`${testIdPrefix}-table`}>
      <TableHeader>
        <TableRow>
          <TableHead>{t("je.column.account")}</TableHead>
          <TableHead className="text-right">{t("je.column.debit")}</TableHead>
          <TableHead className="text-right">{t("je.column.credit")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ACCOUNT_CATEGORY_ORDER.map((cat) => {
          const accounts = summary.accountsByCategory.get(cat);
          if (!accounts || accounts.length === 0) return null;
          return (
            <Fragment key={cat}>
              <TableRow
                className="bg-muted/30"
                data-testid={`${testIdPrefix}-category-${cat}`}
              >
                <TableCell
                  colSpan={3}
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {t(`je.category.${cat}` as never) as string}
                </TableCell>
              </TableRow>
              {accounts.map((acc) => (
                <TableRow
                  key={`${cat}-${acc.accountEn}`}
                  data-testid={`${testIdPrefix}-account-${slug(acc.accountEn)}`}
                >
                  {/* MINI_FIX_BT — per-row explainer popover. (i) icon sits
                      inline next to the account label; click opens a
                      Popover with a plain-language explanation + a
                      separate "Standart dayanağı" line citing the
                      IFRS/IAS provision. Explanation is keyed by the
                      normalized accountEn (per-class variants collapse to
                      one key); unknown accounts fall back to `generic`. */}
                  <TableCell className="pl-6">
                    <span className="inline-flex items-center gap-1">
                      <span>{acc.accountTr}</span>
                      <AccountExplainerPopover
                        acc={acc}
                        currency={currency}
                        testIdPrefix={testIdPrefix}
                      />
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {acc.debit > 0
                      ? formatCurrency(acc.debit.toFixed(2), currency)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {acc.credit > 0
                      ? formatCurrency(acc.credit.toFixed(2), currency)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          );
        })}
        <TableRow
          className="border-t-2 font-bold"
          data-testid={`${testIdPrefix}-total`}
        >
          <TableCell className="font-bold border-t-2 border-border">
            {totalLabel}
          </TableCell>
          <TableCell className="text-right font-mono tabular-nums font-bold border-t-2 border-border">
            {formatCurrency(summary.totalDebit.toFixed(2), currency)}
          </TableCell>
          <TableCell className="text-right font-mono tabular-nums font-bold border-t-2 border-border">
            {formatCurrency(summary.totalCredit.toFixed(2), currency)}{" "}
            {/* SPEC-08 §4.3 — the visible glyph stays the literal ✓/⚠
                because JE tab tests pin the textContent. The colour is
                desaturated for `immaterial` (≤ kuruş rounding) so the
                badge no longer reads as a real warning; a genuine
                material break (> threshold) still renders destructive.
                The `<TieOutBadge/>` primitive is shipped and adopted
                in the per-tab Yevmiye SPEC, which will also update
                those tests. */}
            <span
              className={cn(
                summary.balanced
                  ? "text-emerald-600 dark:text-emerald-400"
                  : tieOutVariant(
                        summary.totalDebit - summary.totalCredit,
                      ) === "immaterial"
                    ? "text-muted-foreground"
                    : "text-destructive",
              )}
              data-testid={`${testIdPrefix}-balance`}
            >
              {summary.balanced ? "✓" : "⚠"}
            </span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    </div>
  );
}

// ─── AccountExplainerPopover (MINI_FIX_BT) ────────────────────────────────

/**
 * MINI_FIX_BT — per-row "Bu tutar nasıl oluştu?" popover trigger.
 *
 * Pure presentation: the explainer text + IFRS/IAS citation come from
 * `je.explainer.<key>` + `je.explainer.<key>.std` in the locale files,
 * where `<key>` is the normalized `accountEn` (see `accountExplainerKey`).
 * Amounts are pre-formatted via the existing `formatCurrency` helper so
 * the i18n templates can interpolate `{{debit}}` / `{{credit}}` / `{{net}}`
 * directly without re-parsing.
 */
function AccountExplainerPopover({
  acc,
  currency,
  testIdPrefix,
}: {
  acc: AccountConsolidation;
  currency: string;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  // The shared TFn cast lets react-i18next accept dynamic keys + params
  // (same workaround as elsewhere in the project where typed `t` overloads
  // reject a `string` key with an object params arg).
  const tDyn = t as unknown as TFn;
  const key = accountExplainerKey(acc.accountEn);
  const debit = formatCurrency(acc.debit.toFixed(2), currency);
  const credit = formatCurrency(acc.credit.toFixed(2), currency);
  const net = formatCurrency((acc.debit - acc.credit).toFixed(2), currency);
  const accountSlug = slug(acc.accountEn);
  const heading = tDyn("je.explainer.popoverHeading");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={tDyn("je.explainer.ariaLabel", {
            account: acc.accountTr,
          })}
          className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid={`${testIdPrefix}-explain-${accountSlug}`}
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="max-w-xs text-sm"
        data-testid={`${testIdPrefix}-explain-content-${accountSlug}`}
      >
        <p className="text-xs font-semibold text-foreground mb-1">
          {heading}
        </p>
        <p className="text-sm text-muted-foreground">
          {tDyn(`je.explainer.${key}`, { debit, credit, net })}
        </p>
        <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
          <p className="font-medium text-foreground mb-0.5">
            {tDyn("je.explainer.stdLabel")}
          </p>
          <p>{tDyn(`je.explainer.${key}.std`)}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── MonthGroup ────────────────────────────────────────────────────────────

export interface MonthGroupProps {
  monthKey: string;
  jes: AggregatedJournalEntry[];
  currency: string;
  indexOffset: number;
  /**
   * Phase 6a.18 — engagement-level Yevmiye tab keeps this `true` so each
   * open month foots a Big4 consolidated summary table. The per-contract
   * page passes `false` because account-level rollups are an
   * engagement-wide concept that don't add information at the single
   * contract granularity.
   */
  showMonthlySummary?: boolean;
  /**
   * Forwarded to each rendered `JECard`. See `JECardProps.showPerContractPlaceholder`.
   */
  showPerContractPlaceholder?: boolean;
  /**
   * Phase 6a.18b — required when `showPerContractPlaceholder` is `true`,
   * because the placeholder is now a list of clickable contractId links
   * back to the per-contract detail page. Forwarded to each JECard.
   */
  engagementId?: string;
}

export function MonthGroup({
  monthKey,
  jes,
  currency,
  indexOffset,
  showMonthlySummary = true,
  showPerContractPlaceholder = true,
  engagementId,
}: MonthGroupProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const monthDate = parseISO(`${monthKey}-01`);
  const locale = i18n.language?.startsWith("tr") ? trLocale : enUS;
  const monthLabel = format(monthDate, "LLLL yyyy", { locale });
  const monthLabelCap =
    monthLabel.charAt(0).toLocaleUpperCase("tr-TR") + monthLabel.slice(1);

  const { totalDebit: monthDebit, totalCredit: monthCredit } = useMemo(
    () => sumDrCr(jes),
    [jes],
  );
  const monthBalanced = Math.abs(monthDebit - monthCredit) < 0.01;

  const monthSummary = useMemo(
    () =>
      open && showMonthlySummary ? consolidateJEs(jes, t as unknown as TFn) : null,
    [open, jes, t, showMonthlySummary],
  );

  return (
    <Card className="p-0 overflow-hidden" data-testid={`je-group-${monthKey}`}>
      <button
        type="button"
        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid={`je-group-${monthKey}-trigger`}
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-semibold">{monthLabelCap}</span>
          <span className="text-sm text-muted-foreground">
            ({t("je.group.entryCount", { count: jes.length })})
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm font-mono tabular-nums">
          <span data-testid={`je-group-${monthKey}-dr`}>
            Dr: {formatCurrency(monthDebit.toFixed(2), currency)}
          </span>
          <span data-testid={`je-group-${monthKey}-cr`}>
            Cr: {formatCurrency(monthCredit.toFixed(2), currency)}
          </span>
          <span
            className={cn(
              "font-sans",
              monthBalanced
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-amber-600 dark:text-amber-400",
            )}
            data-testid={`je-group-${monthKey}-balance`}
          >
            {monthBalanced ? "✓" : "⚠"}
          </span>
        </div>
      </button>

      {open && (
        <div
          className="px-4 pb-4 space-y-3"
          data-testid={`je-group-${monthKey}-content`}
        >
          {jes.map((je, idx) => (
            <JECard
              key={`${je.period}-${je.type}-${idx}`}
              je={je}
              jeNumber={indexOffset + idx + 1}
              currency={currency}
              showPerContractPlaceholder={showPerContractPlaceholder}
              engagementId={engagementId}
            />
          ))}

          {monthSummary && (
            <div data-testid={`je-group-${monthKey}-summary`}>
              <ConsolidatedSummaryTable
                summary={monthSummary}
                totalLabel={t("je.row.monthTotal", {
                  month: monthLabelCap.toLocaleUpperCase("tr-TR"),
                })}
                currency={currency}
                testIdPrefix={`je-group-${monthKey}-summary`}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── JECard ────────────────────────────────────────────────────────────────

export interface JECardProps {
  je: AggregatedJournalEntry;
  jeNumber: number;
  currency: string;
  /**
   * Phase 6a.18b — when `true`, the JE foots a list of clickable
   * contractId links back to the per-contract detail page (with
   * `?tab=journal` so the auditor lands on the Yevmiye inner tab). The
   * per-contract page passes `false` because the page itself IS the
   * per-contract drill-down — surfacing the same links there would be
   * tautological. Replaces the Phase 6a.18 "coming soon" placeholder.
   */
  showPerContractPlaceholder?: boolean;
  /**
   * Phase 6a.18b — engagement id required to construct the per-contract
   * detail URL. Threaded explicitly through props (no React Context)
   * from JournalEntriesTab → MonthGroup → JECard. Optional only because
   * the per-contract page passes `showPerContractPlaceholder={false}`
   * and therefore doesn't need to build any link.
   */
  engagementId?: string;
}

export function JECard({
  je,
  jeNumber,
  currency,
  showPerContractPlaceholder = true,
  engagementId,
}: JECardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Phase 6a.16 — sum / balance moved into the shared JELinesTable
  // primitive. JECard only owns header chrome + the per-contract drill
  // placeholder; the table renders itself self-contained.
  const contractIdsDisplay =
    je.contractIds.slice(0, 3).join(", ") +
    (je.contractIds.length > 3 ? ", ..." : "");
  const periodFormatted = format(parseISO(je.period), "yyyy-MM-dd");
  const padded = String(jeNumber).padStart(3, "0");
  const typeLabel = t(`je.type.${je.type}` as never) as string;

  return (
    <div
      className="border rounded-md overflow-hidden"
      data-testid={`je-card-${padded}`}
    >
      <button
        type="button"
        className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/20 transition-colors text-sm"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid={`je-card-${padded}-trigger`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span
            className="font-medium truncate"
            data-testid={`je-card-${padded}-header`}
          >
            JE-{padded} · {periodFormatted} · {typeLabel} · {je.contractCount}
            {contractIdsDisplay && ` (${contractIdsDisplay})`}
          </span>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3" data-testid={`je-card-${padded}-content`}>
          {/* Phase 6a.16 — Dr/Cr rendering extracted into a shared
              `JELinesTable` primitive so the new PAJE tab can render
              `suggestedJE[]` with byte-identical chrome (TOPLAM row +
              balance indicator). Testids preserved via testIdPrefix. */}
          <JELinesTable
            lines={je.lines}
            currency={currency}
            testIdPrefix={`je-card-${padded}`}
          />

          {/* Phase 6a.18b — placeholder replaced with a clickable list
              of contractId links back to the per-contract detail page
              (Yevmiye inner tab as the source-aware default). Falls
              back to nothing when no engagementId or no contractIds
              are in scope; the per-contract page passes
              `showPerContractPlaceholder={false}` to suppress the row
              entirely. */}
          {showPerContractPlaceholder &&
            engagementId &&
            je.contractIds.length > 0 && (
              <p
                className="mt-3 px-1 text-xs text-muted-foreground"
                data-testid={`je-card-${padded}-percontract`}
              >
                <span className="font-medium">
                  {t("je.perContract.label")}:
                </span>{" "}
                {je.contractIds.map((cid, i) => (
                  <Fragment key={cid}>
                    <Link
                      to={buildContractDetailPath(
                        engagementId,
                        cid,
                        "journal",
                      )}
                      className="underline hover:text-foreground"
                      data-testid={`je-card-${padded}-contract-link-${cid}`}
                    >
                      {cid}
                    </Link>
                    {i < je.contractIds.length - 1 && ", "}
                  </Fragment>
                ))}
              </p>
            )}
        </div>
      )}
    </div>
  );
}
